import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Disclaimer from 'components/disclaimer-section';
import ResultsGrid from 'features/results-grid';
import ResultsRestartButton from 'features/results-restart-button';
import TopActions from 'features/top-actions';
import { CONCRETE_ROUTES, HS_FORM_PORTAL, HS_FORM_REGION } from 'shared/constants';
import useIsMobile from 'shared/hooks/useIsMobile';
import useRestartCalculator from 'shared/hooks/useRestartCalculator';
import useSessionStorage from 'shared/hooks/useSessionStorage';
import { SESSION_STORAGE_KEYS } from 'shared/sessionStorageUtils';
import {
  CalculationInputParamsData,
  ConcreteCalculationInputParamsData,
} from 'shared/types';
import useGetConcreteCalculationsList from '../api/useGetConcreteCalculationsList';
import { MobileWrapper, ResultsLayout, Title } from './styles';
import ModalHeader from '../../../components/modal/modal-header';
import HubspotForm from '../../../components/modal/modal-form';
import ModalComponent from '../../../components/modal';
import ConcreteResultFilters from '../../../components/result-filters/concrete-filters';


const ConcreteResults = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const restartCalculator = useRestartCalculator();
  const [, setinputParametersFromResults] = useSessionStorage(
    SESSION_STORAGE_KEYS.inputParametersFromResults
  );

  const [isRecalculating, setIsRecalculating] = useState(false);

  const {
    productsWithCalculations,
    getConcreteCalculationsList,
    isLoading: isCalculationsListLoading,
  } = useGetConcreteCalculationsList();

  const navigateBack = () => navigate(CONCRETE_ROUTES.fireRating);

  const viewProductDetails = (inputParams: CalculationInputParamsData) => {
    const concreteInputParams =
      inputParams as ConcreteCalculationInputParamsData;
    setinputParametersFromResults({
      productId: concreteInputParams.productId,
      category: concreteInputParams.category,
      fireRatingId: concreteInputParams.fireRatingId,
      orientationId: concreteInputParams.orientationId,
      totalAreaToProtectM2: concreteInputParams.totalAreaToProtectM2,
      requiredAxisDistance: concreteInputParams.requiredAxisDistance,
      currentAxisDistance: concreteInputParams.currentAxisDistance,
    });
    navigate(
      `${CONCRETE_ROUTES.productDetails}/${concreteInputParams.productId}`
    );
  };

  useEffect(() => {
    getConcreteCalculationsList();
  }, []);

  useEffect(() => {
    if(isRecalculating) {
      getConcreteCalculationsList();
      setIsRecalculating(false);
    }
  }, [isRecalculating]);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalData, setModalData] = useState({
    title: '',
    portalId: '',
    formId: '',
    region: '',
  });

  const onContactSupportModalOpen = () => {
    setModalData({
      title: t('modal.contactSupport'),
      portalId: HS_FORM_PORTAL,
      formId: t('hubspot.contactSupportFormId'),
      region: HS_FORM_REGION,
    });

    setIsModalOpen(true);
  };

  return( <div>
      <ModalComponent
        header={
          <ModalHeader
            title={modalData.title}
            handleClose={() => {
              setIsModalOpen(false);
            }}
          />
        }
        isOpen={isModalOpen}
        content={
          <HubspotForm
            portalId={modalData.portalId}
            formId={modalData.formId}
            region={modalData.region}
            onFormSubmitted={() =>
              setModalData((prevState) => ({ ...prevState, title: '' }))
            }
            onDoneClick={() => {
              setIsModalOpen(false);
            }}
          />
        }
      />
      <TopActions
        title={t('results.title') ?? ''}
        onBackClick={navigateBack}
        rightAction={<ResultsRestartButton onClick={restartCalculator} />}
      />
      {isMobile ? (
        <MobileWrapper>
          <ConcreteResultFilters setIsRecalculating={setIsRecalculating}/>
          <ResultsGrid
            isLoadingData={isCalculationsListLoading}
            products={productsWithCalculations}
            onViewProductDetails={viewProductDetails}
            onContactSupportModalOpen={onContactSupportModalOpen}
          />
          { !isCalculationsListLoading &&
            <Disclaimer text={t('results.legal')} />
          }
        </MobileWrapper>
      ) : (
        <>
          <Title title={t('results.title')} />
          <ResultsLayout>
            <ConcreteResultFilters setIsRecalculating={setIsRecalculating}/>
            <ResultsGrid
              isLoadingData={isCalculationsListLoading}
              products={productsWithCalculations}
              onViewProductDetails={viewProductDetails}
              onContactSupportModalOpen={onContactSupportModalOpen}
            />
          </ResultsLayout>
          { !isCalculationsListLoading &&
            <Disclaimer text={t('results.legal')} />
          }
        </>
      )}
    </div>
  );
};

export default ConcreteResults;
